/** Read-only bake diagnostics in mesh-local coordinates, with the same effective
 * front-face convention as the glTF exporter. Local coordinates deliberately
 * avoid mistaking mirrored node transforms for reversed source triangles.
 * Positional welding joins normal/material seams; this is evidence, not a
 * manifold, self-intersection, nesting or instruction-fidelity certificate.
 */
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Material } from '@babylonjs/core/Materials/material';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';

type V = [number, number, number];
const sub = (a: V, b: V): V => [a[0]-b[0], a[1]-b[1], a[2]-b[2]];
const cross = (a: V, b: V): V => [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
const dot = (a: V, b: V): number => a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
interface Counts {
    triangles: number; degenerateTriangles: number; normalDisagreements: number;
    boundaryEdges: number; inconsistentEdges: number; nonManifoldEdges: number;
    closedComponents: number; negativeVolumeComponents: number;
}
export interface MeshDiagnostics extends Counts {
    method: string;
    details: (Counts & { mesh: string })[];
    omittedDetails: number;
    skippedMeshes: number;
}
const counts = (): Counts => ({ triangles: 0, degenerateTriangles: 0, normalDisagreements: 0,
    boundaryEdges: 0, inconsistentEdges: 0, nonManifoldEdges: 0, closedComponents: 0, negativeVolumeComponents: 0 });

/** Work bounded independently of the author's build budget (unindexed geometry
 * and shared/cloned buffers may otherwise escape a simple index-count limit). */
export function diagnoseMeshes(meshes: Mesh[]): MeshDiagnostics {
    const out: MeshDiagnostics = { ...counts(), method: 'Local front-face orientation; positional weld tolerance max(1e-9, extent * 1e-7). Closed means every welded edge has exactly two opposite uses. Negative volume can be an intentional cavity shell. No geometry changed.', details: [], omittedDetails: 0, skippedMeshes: 0 };
    let remainingVertices = 900_000, remainingTriangles = 200_000;
    for (const mesh of meshes) {
        const p = mesh.getVerticesData(VertexBuffer.PositionKind);
        const indices = mesh.getIndices();
        const n = mesh.getVerticesData(VertexBuffer.NormalKind);
        const indexCount = indices?.length || (p?.length ?? 0) / 3;
        if (!p || p.length / 3 > remainingVertices || indexCount / 3 > remainingTriangles || indexCount % 3) { out.skippedMeshes++; continue; }
        remainingVertices -= p.length / 3; remainingTriangles -= indexCount / 3;
        const row = { ...counts(), mesh: mesh.name.slice(0, 100) };
        const min: V = [Infinity, Infinity, Infinity], max: V = [-Infinity, -Infinity, -Infinity];
        for (let i = 0; i < p.length; i++) { const k = i % 3; min[k] = Math.min(min[k], p[i]); max[k] = Math.max(max[k], p[i]); }
        if (![...min, ...max].every(Number.isFinite)) { out.skippedMeshes++; continue; }
        const extent = Math.max(...max.map((v, k) => v-min[k]));
        const epsilon = Math.max(1e-9, extent * 1e-7);
        const origin = min.map((v, k) => (v+max[k])/2) as V;
        const vertices: V[] = [], welded: number[] = [], ids = new Map<string, number>();
        for (let i = 0; i < p.length; i += 3) {
            const v: V = [p[i], p[i+1], p[i+2]];
            vertices.push(sub(v, origin));
            const key = v.map((x, k) => Math.round((x-min[k])/epsilon)).join(',');
            let id = ids.get(key); if (id === undefined) { id = ids.size; ids.set(key, id); }
            welded.push(id);
        }
        const parents: number[] = [], volumes: number[] = [];
        const find = (i: number): number => { while (parents[i] !== i) { parents[i] = parents[parents[i]]; i = parents[i]; } return i; };
        const edges = new Map<string, { first: number; uses: number; balance: number }>();
        // Export orientation can differ by material within one mesh. Assemble
        // components across those boundaries before evaluating signed volume.
        const orientations = new Int8Array(indexCount / 3).fill(mesh.sideOrientation === Material.CounterClockWiseSideOrientation ? 1 : -1);
        let unsupported = false;
        for (const sm of mesh.subMeshes) {
            const mat = sm.getMaterial();
            if (mat && ![Material.TriangleFillMode, Material.WireFrameFillMode, Material.PointFillMode].includes(mesh.overrideRenderingFillMode ?? mat.fillMode)) { unsupported = true; break; }
            const sign = (mat?.sideOrientation ?? mesh.sideOrientation) === Material.CounterClockWiseSideOrientation ? 1 : -1;
            const start = indices?.length ? sm.indexStart : sm.verticesStart;
            const count = indices?.length ? sm.indexCount : sm.verticesCount;
            orientations.fill(sign, start / 3, (start+count) / 3);
        }
        if (unsupported) { out.skippedMeshes++; continue; }
        for (let i = 0; i < indexCount; i += 3) {
            const ix = [0,1,2].map(k => indices?.length ? indices[i+k] : i+k);
            if (orientations[i/3] < 0) [ix[1], ix[2]] = [ix[2], ix[1]];
            const [a,b,c] = ix.map(k => vertices[k]);
            if (!a || !b || !c) { unsupported = true; break; }
            row.triangles++;
            const face = cross(sub(b,a), sub(c,a));
            if (dot(face,face) <= epsilon**4) { row.degenerateTriangles++; continue; }
            if (n) {
                const normal = [0,1,2].map(k => n[ix[0]*3+k]+n[ix[1]*3+k]+n[ix[2]*3+k]) as V;
                if (dot(face,normal) < -1e-6 * Math.sqrt(dot(face,face)*dot(normal,normal))) row.normalDisagreements++;
            }
            const t = parents.length; parents.push(t); volumes.push(dot(a,cross(b,c))/6);
            for (let k = 0; k < 3; k++) {
                const x = welded[ix[k]], y = welded[ix[(k+1)%3]];
                const key = x < y ? `${x},${y}` : `${y},${x}`;
                const edge = edges.get(key), direction = x < y ? 1 : -1;
                if (edge) { parents[find(t)] = find(edge.first); edge.uses++; edge.balance += direction; }
                else edges.set(key, { first: t, uses: 1, balance: direction });
            }
        }
        if (unsupported) { out.skippedMeshes++; continue; }
        const open = new Set<number>();
        for (const edge of edges.values()) {
            if (edge.uses === 1) row.boundaryEdges++;
            else if (edge.uses > 2) row.nonManifoldEdges++;
            else if (edge.balance !== 0) row.inconsistentEdges++;
            if (edge.uses !== 2 || edge.balance !== 0) open.add(find(edge.first));
        }
        const volume = new Map<number, number>();
        for (let t = 0; t < parents.length; t++) { const root = find(t); volume.set(root, (volume.get(root) ?? 0)+volumes[t]); }
        for (const [root, v] of volume) if (!open.has(root)) {
            row.closedComponents++;
            if (v < -(epsilon**3)) row.negativeVolumeComponents++;
        }
        for (const key of Object.keys(counts()) as (keyof Counts)[]) out[key] += row[key];
        if (row.negativeVolumeComponents || row.normalDisagreements || row.boundaryEdges || row.inconsistentEdges || row.nonManifoldEdges || row.degenerateTriangles) {
            if (out.details.length < 8) out.details.push(row); else out.omittedDetails++;
        }
    }
    return out;
}

export function diagnosticWarnings(d: MeshDiagnostics): string[] {
    const warnings: string[] = [];
    if (d.negativeVolumeComponents || d.normalDisagreements || d.inconsistentEdges) {
        const names = d.details.filter(r => r.negativeVolumeComponents || r.normalDisagreements || r.inconsistentEdges).map(r => JSON.stringify(r.mesh)).join(', ');
        warnings.push(`Geometry inspection: ${d.negativeVolumeComponents} closed component(s) have negative signed volume (inward if standalone; cavity shells may be intentional), ${d.normalDisagreements} triangle(s) disagree with vertex normals, ${d.inconsistentEdges} edge(s) have inconsistent face directions. Affected meshes include ${names || 'details omitted'}. Inspect and revise unintended face winding/normals before duplicating; fromPydata/meshFromVertexData default to CCW viewed from outside. Geometry was not changed.`);
    }
    if (d.boundaryEdges || d.nonManifoldEdges || d.degenerateTriangles) warnings.push(`Topology inspection: ${d.boundaryEdges} boundary edges, ${d.nonManifoldEdges} non-manifold edges, ${d.degenerateTriangles} degenerate triangles after positional welding. Open/double-sided surfaces can be intentional; closed-solid orientation cannot be inferred for these components.`);
    if (d.skippedMeshes) warnings.push(`Geometry inspection skipped ${d.skippedMeshes} mesh(es) with unsupported data or beyond its 200,000-triangle / 900,000-vertex work budget; no completeness claim.`);
    return warnings;
}
